import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, ShoppingCart, Activity, Search, 
  Plus, Download, RefreshCw, Eye, Lock, LogOut, X,
  ChevronDown, Clock, QrCode, Database,
  CheckCircle
} from 'lucide-react';
import { db, auth } from '../firebase';
import { 
  collection, doc, onSnapshot, query, where, 
  orderBy, addDoc, updateDoc, serverTimestamp, getDocs
} from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe('pk_live_51RoXfVGYHuUUMTi2hNtoxJpqlrNLFufqy85Z10IepfSz7VVmS8zzEpCgEyUKQDI1yXUl9nq04NEa7l8fZALi55zV00X3mi71Q7');
 
const BASE_URL = 'https://olahbackend.gaia-ml.com/api';
 
const OrganizationSwitcher = ({ user, currentBusinessId, setCurrentBusinessId, accessibleBusinessIds }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!user || accessibleBusinessIds.length <= 1) {
    return null; // Don't show switcher if only one org
  }

  const getCurrentOrgName = () => {
    if (currentBusinessId === user.uid) {
      return 'My Organization';
    }
    
    const membership = user.customClaims?.teamMemberships?.[currentBusinessId];
    if (membership) {
      return `${membership.businessEmail}'s Organization`;
    }
    
    return 'Organization';
  };

  const getCurrentRole = () => {
    if (currentBusinessId === user.uid) {
      return 'Owner';
    }
    
    const membership = user.customClaims?.teamMemberships?.[currentBusinessId];
    return membership?.role === 'editor' ? 'Editor' : 'Reader';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 border border-gray-200 text-sm hover:bg-gray-100 transition"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <div className="text-left min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">
              {getCurrentOrgName()}
            </div>
            <div className="text-xs text-gray-500 uppercase truncate">
              {getCurrentRole()}
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-sm z-50 overflow-hidden max-w-full">
            {/* My Organization */}
            <button
              onClick={() => {
                setCurrentBusinessId(user.uid);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition ${
                currentBusinessId === user.uid ? 'bg-gray-100 border-l-2 border-black' : ''
              }`}
            >
              <Building2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  My Organization
                </div>
                <div className="text-xs text-gray-500 uppercase">Owner</div>
              </div>
              {currentBusinessId === user.uid && (
                <div className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0" />
              )}
            </button>

            {/* Team Memberships */}
            {Object.entries(user.customClaims?.teamMemberships || {}).map(([businessId, membership]) => (
              <button
                key={businessId}
                onClick={() => {
                  setCurrentBusinessId(businessId);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition border-t border-gray-200 ${
                  currentBusinessId === businessId ? 'bg-gray-100 border-l-2 border-black' : ''
                }`}
              >
                <Users className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <div className="text-left flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {membership.businessEmail}'s Organization
                  </div>
                  <div className="text-xs text-gray-500 uppercase truncate">
                    {membership.role === 'editor' ? 'Editor' : 'Reader'}
                  </div>
                </div>
                {currentBusinessId === businessId && (
                  <div className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const EsimDetailsScreen = ({ handleBusinessDownloadQR, handleRevokeEsim, onBack, esim, isLoading }) => {
  const [loadingBundleStatus, setLoadingBundleStatus] = useState(false);
  const [bundleStatusData, setBundleStatusData] = useState(null);
  const [bundleStatusError, setBundleStatusError] = useState(null);

  useEffect(() => {
    if (esim) {
      fetchBundleStatus();
    }
  }, [esim]);

  const fetchBundleStatus = async () => {
    if (!esim || !auth.currentUser) return;
    
    setLoadingBundleStatus(true);
    setBundleStatusError(null);
    setBundleStatusData(null);
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${BASE_URL}/business/esims/${esim.id}/status/`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBundleStatusData(data);
      } else {
        const errorData = await response.json();
        setBundleStatusError(errorData.error || 'Failed to fetch bundle status');
      }
    } catch (e) {
      console.error('Error fetching bundle status:', e);
      setBundleStatusError('Network error - unable to fetch status');
    } finally {
      setLoadingBundleStatus(false);
    }
  };

  const handleRefreshBundleStatus = async () => {
    await fetchBundleStatus();
  };

  const formatFullDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit', 
        timeZoneName: 'short'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getBundleStateColor = (bundleState) => {
    switch ((bundleState || '').toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'queued':
      case 'processing': return 'bg-yellow-100 text-yellow-700';
      case 'depleted': return 'bg-orange-100 text-orange-700';
      case 'expired':
      case 'lapsed': return 'bg-gray-100 text-gray-700';
      case 'revoked': return 'bg-red-100 text-red-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getBundleStateIcon = (bundleState) => {
    switch ((bundleState || '').toLowerCase()) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'queued':
      case 'processing': return <Clock className="w-4 h-4" />;
      case 'depleted': return <Activity className="w-4 h-4" />;
      case 'expired':
      case 'lapsed': return <Clock className="w-4 h-4" />;
      case 'revoked': return <X className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard');
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  };
  

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="p-2 hover:bg-gray-100 rounded transition disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h2 className="text-2xl font-light">eSIM Details</h2>
          <p className="text-sm text-gray-500 mt-1">
            {esim.country} • {esim.planName}
          </p>
        </div>
      </div>

      {/* eSIM Summary */}
      <div className="border border-gray-200 p-6">
        <div className="text-xs text-gray-500 mb-3 uppercase">eSIM Information</div>
        <div className="grid grid-cols-4 gap-6">
          <div>
            <div className="text-sm text-gray-500 mb-1">ICCID</div>
            <div className="font-mono text-sm">{esim.iccid}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Assigned To</div>
            <div className="text-sm">{esim.assignedEmail || <span className="text-gray-400 italic">Unassigned</span>}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Status</div>
            <span className={`text-xs uppercase px-2.5 py-1 rounded ${
              esim.status === 'active' 
                ? 'bg-green-100 text-green-700' 
                : esim.status === 'ready' 
                ? 'bg-blue-100 text-blue-700' 
                : esim.status === 'revoked'
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {esim.status}
            </span>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Purchased At</div>
            <div className="text-sm">{formatFullDate(esim.purchasedAt)}</div>
          </div>
        </div>
      </div>

      {/* Live Bundle Status */}
      <div className="border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-gray-500 mb-1 uppercase">Live Usage Status</div>
            <div className="text-sm text-gray-600">Real-time data from provider</div>
          </div>
          <button
            onClick={handleRefreshBundleStatus}
            disabled={loadingBundleStatus}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition disabled:opacity-50"
            title="Refresh status"
          >
            <RefreshCw className={`w-4 h-4 ${loadingBundleStatus ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loadingBundleStatus && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
              <span className="text-sm text-gray-600">Fetching live status...</span>
            </div>
          </div>
        )}

        {bundleStatusError && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
            <div className="flex items-center">
              <X className="w-4 h-4 mr-2" />
              {bundleStatusError}
            </div>
          </div>
        )}

        {bundleStatusData && !loadingBundleStatus && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded text-xs uppercase font-medium ${getBundleStateColor(bundleStatusData.bundle_status.bundle_state)}`}>
                {getBundleStateIcon(bundleStatusData.bundle_status.bundle_state)}
                {bundleStatusData.bundle_status.bundle_state || 'UNKNOWN'}
              </span>
              {bundleStatusData.bundle_status.assignment_reference && (
                <div className="text-xs text-gray-500 font-mono">
                  Ref: {bundleStatusData.bundle_status.assignment_reference}
                </div>
              )}
            </div>

            {bundleStatusData.bundle_status.initial_quantity > 0 && (
              <div className="border border-gray-200 p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-900">Data Usage</span>
                  <span className="text-sm text-gray-600">{bundleStatusData.bundle_status.usage_percent}%</span>
                </div>
                <div className="w-full bg-gray-200 h-2 mb-3">
                  <div
                    className="bg-green-600 h-2 transition-all duration-500"
                    style={{ width: `${Math.min(bundleStatusData.bundle_status.usage_percent, 100)}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div>
                    <div className="text-gray-500 mb-1">Used</div>
                    <div className="font-semibold text-gray-900">{bundleStatusData.bundle_status.used_quantity_formatted}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Remaining</div>
                    <div className="font-semibold text-green-600">{bundleStatusData.bundle_status.remaining_quantity_formatted}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Total</div>
                    <div className="font-semibold text-gray-900">{bundleStatusData.bundle_status.initial_quantity_formatted}</div>
                  </div>
                </div>
              </div>
            )}

            {bundleStatusData.bundle_status.assignment_date && (
              <div className="border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-1">Assignment Date</div>
                <div className="text-sm font-medium text-gray-900">
                  {formatFullDate(bundleStatusData.bundle_status.assignment_date)}
                </div>
              </div>
            )}

            {bundleStatusData.bundle_status.last_updated && (
              <div className="text-xs text-gray-500">
                Last updated: {new Date(bundleStatusData.bundle_status.last_updated).toLocaleTimeString()}
              </div>
            )}

            {bundleStatusData.bundle_status.message && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded text-sm">
                {bundleStatusData.bundle_status.message}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Technical Details */}
      <div className="border border-gray-200 p-6">
        <div className="text-xs text-gray-500 mb-4 uppercase">Technical Details</div>
        <div className="space-y-3">
          <div>
            <div className="text-xs text-gray-500 mb-1">ICCID</div>
            <div className="flex items-center justify-between border border-gray-200 p-3">
              <code className="text-sm font-mono text-gray-900 break-all">{esim.iccid}</code>
              <button
                onClick={() => copyToClipboard(esim.iccid)}
                className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                title="Copy ICCID"
              >
                <Database className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {esim.qr_code_data && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Activation Code</div>
              <div className="flex items-center justify-between border border-gray-200 p-3">
                <code className="text-sm font-mono text-gray-900 break-all pr-2">{esim.qr_code_data}</code>
                <button
                  onClick={() => copyToClipboard(esim.qr_code_data)}
                  className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  title="Copy activation code"
                >
                  <QrCode className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          {esim.goesim_order_reference && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Order Reference</div>
              <div className="border border-gray-200 p-3">
                <code className="text-sm font-mono text-gray-900">{esim.goesim_order_reference}</code>
              </div>
            </div>
          )}
        </div>
      </div>

{/* Actions */}
<div className="flex gap-4 pt-4">
  <button
    onClick={onBack}
    className="px-6 py-3 border border-gray-200 text-sm hover:bg-gray-50 transition"
  >
    Back to eSIMs List
  </button>
  <button 
    onClick={() => handleBusinessDownloadQR(esim)}
    className="px-6 py-3 bg-black text-white text-sm hover:bg-gray-800 transition"
  >
    Download QR Code
  </button>
  {/* Add Revoke Button */}
  {esim.status !== 'revoked' && (
    <button 
      onClick={() => handleRevokeEsim(esim)}
      disabled={isLoading}
      className="px-6 py-3 border border-red-200 text-red-600 text-sm hover:bg-red-50 transition disabled:opacity-50"
    >
      Revoke Bundle
    </button>
  )}
</div>
    </div>
  );
};

const BusinessAdmin = () => {
  const navigate = useNavigate();
  const [viewingEsimDetails, setViewingEsimDetails] = useState(false);
  const [selectedEsimForDetails, setSelectedEsimForDetails] = useState(null);
  const [currentBusinessId, setCurrentBusinessId] = useState(null);
  const [accessibleBusinessIds, setAccessibleBusinessIds] = useState([]);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [user, setUser] = useState(null);
  const [provisioningOrder, setProvisioningOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [businessProfile, setBusinessProfile] = useState(null);
  const [preselectedOrderData, setPreselectedOrderData] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [showCountryPlansModal, setShowCountryPlansModal] = useState(false);
  const [selectedCountryPlans, setSelectedCountryPlans] = useState(null);
  // Data states
  const [bulkOrders, setBulkOrders] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [purchasedEsims, setPurchasedEsims] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [notification, setNotification] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showBulkOrderModal, setShowBulkOrderModal] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalSpent: 0,
    totalEsims: 0,
    activeEsims: 0,
    teamMembers: 0
  });

  // Auth listener
useEffect(() => {

  const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      setUser(firebaseUser);
      setHasAccess(true); // Always grant access
      loadBusinessData(firebaseUser.uid);
    } else {
      setUser(null);
      setHasAccess(false);
    }
    setLoading(false);
  });
  return () => unsubscribe();
}, []);
 

useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      // Force token refresh to get latest custom claims
      await firebaseUser.getIdToken(true);
      
      // Get the token to check claims
      const tokenResult = await firebaseUser.getIdTokenResult();
      console.log('Team memberships:', tokenResult.claims.teamMemberships);
      
      setUser(firebaseUser);
      loadBusinessData(firebaseUser.uid);
    }
  });
  return () => unsubscribe();
}, []);

const handleViewEsimDetails = (esim) => {
  setSelectedEsimForDetails(esim);
  setViewingEsimDetails(true);
};

const handleBusinessDownloadQR = async (esim) => {
  try {
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/business/esims/${esim.id}/download-qr/`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to download QR code');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `qr_code_${esim.iccid}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    showNotification('QR code downloaded successfully!');
  } catch (error) {
    console.error('Error downloading QR code:', error);
    showNotification('Failed to download QR code');
  }
};

const handleRevokeEsim = async (esim) => {
  // Confirmation dialog with warning
  const confirmMessage = `Are you sure you want to revoke this eSIM bundle?\n\n`;

  if (!window.confirm(confirmMessage)) {
    return;
  }

  try {
    setLoading(true);
    const token = await user.getIdToken();
    
    const response = await fetch(`${BASE_URL}/business/esims/${esim.id}/revoke/`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      showNotification('eSIM bundle revoked successfully');
      
      // Show warning if applicable
      if (data.revoke_data?.warning) {
        setTimeout(() => {
          alert(`⚠️ ${data.revoke_data.warning}`);
        }, 500);
      }
      
      // Refresh data
      loadBusinessData(currentBusinessId);
    } else {
      const errorData = await response.json();
      showNotification(errorData.error || 'Failed to revoke eSIM bundle');
      
      if (errorData.details) {
        setTimeout(() => {
          alert(`Error details: ${errorData.details}`);
        }, 500);
      }
    }
  } catch (error) {
    console.error('Error revoking eSIM:', error);
    showNotification('Failed to revoke eSIM bundle');
  } finally {
    setLoading(false);
  }
};

  // Load all business data with real-time listeners
const loadBusinessData = (businessId) => { // Changed parameter name
  const ordersQuery = query(
    collection(db, 'bulk_orders'), 
    where('businessId', '==', businessId), // Use businessId instead of userId
    orderBy('createdAt', 'desc')
  );
  
  const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
    const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setBulkOrders(orders);
    
    const totalSpent = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
    const totalEsims = orders.reduce((sum, order) => sum + (order.quantity || 0), 0);
    setStats(prev => ({ ...prev, totalSpent, totalEsims }));
  });

  const teamQuery = query(
    collection(db, 'business_team_members'), 
    where('businessId', '==', businessId) // Changed
  );
  const unsubTeam = onSnapshot(teamQuery, (snapshot) => {
    const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTeamMembers(members);
    setStats(prev => ({ ...prev, teamMembers: members.length }));
  });

  const esimsQuery = query(
    collection(db, 'business_esims'),
    where('businessId', '==', businessId), // Changed
    orderBy('purchasedAt', 'desc')
  );
  
  const unsubEsims = onSnapshot(esimsQuery, (snapshot) => {
    const esims = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPurchasedEsims(esims);
    const activeCount = esims.filter(e => e.status === 'active').length;
    setStats(prev => ({ ...prev, activeEsims: activeCount }));
  });

  loadAvailablePlans();

  if (hasAccess && user) {
    loadApiKeys();
  }

  return () => {
    unsubOrders();
    unsubTeam();
    unsubEsims();
  };
};

  const loadAvailablePlans = async () => {
    try {
      const countriesSnapshot = await getDocs(collection(db, 'countries'));
      const plans = [];
      
      countriesSnapshot.docs.forEach(doc => {
        const country = doc.data();
        const countryPlans = extractPlansFromCountry(country, doc.id);
        plans.push(...countryPlans);
      });
      
      setAvailablePlans(plans);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const extractPlansFromCountry = (country, countryCode) => {
    const plans = [];
    const markup = country.markup_percentage || 25;

    Object.entries(country.fixed_plans || {}).forEach(([duration, durationPlans]) => {
      durationPlans.forEach(plan => {
        plans.push({
          ...plan,
          countryCode,
          countryName: country.name,
          duration: parseInt(duration),
          finalPrice: (plan.price * (1 + markup / 100)).toFixed(2),
          type: 'fixed',
          dataAmount: plan.data_amount
        });
      });
    });

    Object.entries(country.unlimited_plans || {}).forEach(([duration, durationPlans]) => {
      durationPlans.forEach(plan => {
        plans.push({
          ...plan,
          countryCode,
          countryName: country.name,
          duration: parseInt(duration),
          finalPrice: (plan.price * (1 + markup / 100)).toFixed(2),
          type: 'unlimited',
          dataAmount: -1
        });
      });
    });

    return plans;
  };


const handleDownloadQR = async (esim) => {
  try {
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/business/esims/${esim.id}/download-qr/`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to download QR code');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `qr_code_${esim.iccid}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    showNotification('QR code downloaded successfully!');
  } catch (error) {
    console.error('Error downloading QR code:', error);
    showNotification('Failed to download QR code');
  }
};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Force token refresh to get latest custom claims
        await firebaseUser.getIdToken(true);
        
        // Get the token to check claims
        const tokenResult = await firebaseUser.getIdTokenResult();
        
        // Attach custom claims to user object for easy access
        firebaseUser.customClaims = tokenResult.claims;
        
        console.log('User custom claims:', tokenResult.claims);
        console.log('Team memberships:', tokenResult.claims.teamMemberships);
        
        setUser(firebaseUser);
        
        // Calculate accessible business IDs
        const accessible = [
          firebaseUser.uid, // User's own business
          ...Object.keys(tokenResult.claims.teamMemberships || {}) // Businesses they're a member of
        ];
        
        setAccessibleBusinessIds(accessible);
        
        // Set default business context
        if (accessible.length > 0) {
          // If user has no data in their own org but has memberships, default to first membership
          // Otherwise default to their own org
          const defaultBusinessId = accessible[0];
          setCurrentBusinessId(defaultBusinessId);
        }
        
        setHasAccess(true);
      } else {
        setUser(null);
        setHasAccess(false);
        setCurrentBusinessId(null);
        setAccessibleBusinessIds([]);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  // MODIFY: Update loadBusinessData to use currentBusinessId
  useEffect(() => {
    if (user && currentBusinessId) {
      loadBusinessData(currentBusinessId); // Pass currentBusinessId instead of user.uid
    }
  }, [user, currentBusinessId]);

const handleBulkOrder = async (orderData) => {
  try {
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/business/bulk-orders/?businessId=${currentBusinessId}`, { // Add query param
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        ...orderData,
        businessId: currentBusinessId // Explicit in body
      })
    });

    if (response.ok) {
      const data = await response.json();
      showNotification('Bulk order created successfully!');
      setShowBulkOrderModal(false);
      loadBusinessData(currentBusinessId); // Pass context
    } else {
      const errorData = await response.json();
      showNotification(errorData.error || 'Failed to create order');
    }
  } catch (error) {
    console.error('Error creating bulk order:', error);
    showNotification('Failed to create order');
  }
};

  const handleProvisionOrder = async (assignments, provisionQuantity, organizationName, customMessage) => {
  try {
    setIsProvisioning(true);
    showNotification(`Provisioning ${provisionQuantity} eSIMs... This may take 1-2 minutes.`);
    
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/business/bulk-orders/${provisioningOrder.id}/provision?businessId=${currentBusinessId}`, { // Add param
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        assignments,
        provision_quantity: provisionQuantity,
        organization_name: organizationName,
        custom_message: customMessage
      })
    });

    if (response.ok) {
      const data = await response.json();
      // ... rest of function
      loadBusinessData(currentBusinessId); // Pass context
    }
  } catch (error) {
      console.error('Error provisioning order:', error);
      showNotification('Failed to provision eSIMs');
    } finally {
      setIsProvisioning(false); // Clear provisioning state
    }
  };

const handleAddTeamMember = async (email, role) => {
  if (!hasAccess) {
    setShowPaymentModal(true);
    return;
  }

  try {
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/business/team/add/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email, role })
    });

    if (response.ok) {
      showNotification('Team member added successfully!');
      setShowAddMemberModal(false);
      // Refresh team list
      loadBusinessData(user.uid);
    } else {
      const errorData = await response.json();
      showNotification(errorData.error || 'Failed to add team member');
    }
  } catch (error) {
    console.error('Error adding team member:', error);
    showNotification('Failed to add team member');
  }
};

const handleRemoveTeamMember = async (memberId) => {
  if (!hasAccess) {
    setShowPaymentModal(true);
    return;
  }

  if (!window.confirm('Are you sure you want to remove this team member? They will lose access to all assigned eSIMs.')) {
    return;
  }

  try {
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/business/team/${memberId}/remove/`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      showNotification('Team member removed successfully!');
      // Refresh team list
      loadBusinessData(user.uid);
    } else {
      const errorData = await response.json();
      showNotification(errorData.error || 'Failed to remove team member');
    }
  } catch (error) {
    console.error('Error removing team member:', error);
    showNotification('Failed to remove team member');
  }
};


  const handleGenerateApiKey = async (keyName) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/business/api-keys/generate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: keyName,
          expires_in_days: 365 
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedApiKey(data.api_key);
        showNotification('API key generated successfully!');
        // Refresh API keys list
        loadApiKeys();
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || 'Failed to generate API key');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      showNotification('Failed to generate API key');
    }
  };

  const loadApiKeys = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/business/api-keys/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.api_keys);
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/business/api-keys/${keyId}/revoke/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('API key revoked successfully');
        loadApiKeys();
      } else {
        showNotification('Failed to revoke API key');
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
      showNotification('Failed to revoke API key');
    }
  };


  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/xyz');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const filteredPlans = availablePlans.filter(plan => {
    const matchesSearch = plan.countryName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCountry = selectedCountry === 'all' || plan.countryCode === selectedCountry;
    return matchesSearch && matchesCountry;
  });


  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-light mb-4">Business Portal</h1>
          <p className="text-sm text-gray-600 mb-6">Sign in to access your dashboard</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-black text-white text-sm hover:bg-gray-800 transition"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 bg-black text-white px-4 py-3 text-sm z-50">
          {notification}
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-lg font-medium">Business Portal</h1>
          <p className="text-xs text-gray-500 mt-1">{user.email}</p>
        </div>
        <div className="p-4 border-b border-gray-200">
          <OrganizationSwitcher 
            user={user}
            currentBusinessId={currentBusinessId}
            setCurrentBusinessId={setCurrentBusinessId}
            accessibleBusinessIds={accessibleBusinessIds}
          />
        </div>
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`w-full text-left px-4 py-2 text-sm transition ${
              activeTab === 'overview'
                ? 'bg-white border border-gray-200'
                : 'hover:bg-gray-100'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`w-full text-left px-4 py-2 text-sm transition ${
              activeTab === 'plans'
                ? 'bg-white border border-gray-200'
                : 'hover:bg-gray-100'
            }`}
          >
            <span>Browse Plans</span>
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full text-left px-4 py-2 text-sm transition ${
              activeTab === 'orders'
                ? 'bg-white border border-gray-200'
                : 'hover:bg-gray-100'
            }`}
          >
            <span>Bulk Orders</span>
          </button>
          <button
            onClick={() => setActiveTab('esims')}
            className={`w-full text-left px-4 py-2 text-sm transition ${
              activeTab === 'esims'
                ? 'bg-white border border-gray-200'
                : 'hover:bg-gray-100'
            }`}
          >
            <span>My eSIMs</span>
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`w-full text-left px-4 py-2 text-sm transition ${
              activeTab === 'team'
                ? 'bg-white border border-gray-200'
                : 'hover:bg-gray-100'
            }`}
          >
            <span>Team</span>
          </button>
          <button
            onClick={() => setActiveTab('api-keys')}
            className={`w-full text-left px-4 py-2 text-sm transition ${
              activeTab === 'api-keys'
                ? 'bg-white border border-gray-200'
                : 'hover:bg-gray-100'
            }`}
          >
            <span>API Keys</span>
          </button>
        </div>
      </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 transition"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-light mb-2">Dashboard</h2>
                <p className="text-sm text-gray-500">
                  {hasAccess ? 'Welcome back' : 'Unlock full access to manage your eSIM fleet'}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-6">
                <div className="border border-gray-200 p-6">
                  <div className="text-xs text-gray-500 mb-2">TOTAL SPENT</div>
                  <div className="text-2xl font-light">${stats.totalSpent.toFixed(2)}</div>
                </div>
                <div className="border border-gray-200 p-6">
                  <div className="text-xs text-gray-500 mb-2">TOTAL ESIMS</div>
                  <div className="text-2xl font-light">{stats.totalEsims}</div>
                </div>
                <div className="border border-gray-200 p-6">
                  <div className="text-xs text-gray-500 mb-2">ACTIVE ESIMS</div>
                  <div className="text-2xl font-light">{stats.activeEsims}</div>
                </div>
                <div className="border border-gray-200 p-6">
                  <div className="text-xs text-gray-500 mb-2">TEAM MEMBERS</div>
                  <div className="text-2xl font-light">{stats.teamMembers}</div>
                </div>
              </div>

              {hasAccess && bulkOrders.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium mb-4">RECENT ORDERS</h3>
                  <div className="border border-gray-200">
                    <table className="w-full">
                      <thead className="border-b border-gray-200">
                        <tr>
                          <th className="text-left p-4 text-xs font-medium text-gray-500">PLAN</th>
                          <th className="text-left p-4 text-xs font-medium text-gray-500">QUANTITY</th>
                          <th className="text-left p-4 text-xs font-medium text-gray-500">AMOUNT</th>
                          <th className="text-left p-4 text-xs font-medium text-gray-500">STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkOrders.slice(0, 5).map(order => (
                          <tr key={order.id} className="border-b border-gray-200 last:border-0">
                            <td className="p-4 text-sm">{order.planName}</td>
                            <td className="p-4 text-sm">{order.quantity}</td>
                            <td className="p-4 text-sm">${order.totalAmount?.toFixed(2)}</td>
                            <td className="p-4 text-sm">
                              <span className="text-xs uppercase">{order.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-light">Bulk Orders</h2>
                <button
                  onClick={() => setShowBulkOrderModal(true)}
                  className="px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition"
                  disabled={!hasAccess}
                >
                  New Order
                </button>
              </div>

              {bulkOrders.length === 0 ? (
                <div className="border border-gray-200 p-12 text-center">
                  <p className="text-sm text-gray-500">No orders yet</p>
                </div>
              ) : (
                <div className="border border-gray-200">
                  <table className="w-full">
                    <thead className="border-b border-gray-200">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">COUNTRY</th>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">PLAN</th>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">BOUGHT</th>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">REMAINING</th>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">PAID</th>
                        <th className="text-right p-4 text-xs font-medium text-gray-500">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkOrders.map(order => (
                        <tr key={order.id} className="border-b border-gray-200 last:border-0">
                          <td className="p-4 text-sm font-mono">{order.country}</td>
                          <td className="p-4">
                            <div className="text-sm">{order.planName}</div>
                          </td>
                          <td className="p-4 text-sm">{order.quantity}</td>
                          <td className="p-4">
                            {order.quantity - (order.provisionedCount || 0)}
                          </td>
                          <td className="p-4 text-sm">${order.totalAmount?.toFixed(2)}</td>
                          <td className="px-6 py-4 text-right">
                            {(order.status === 'paid' || order.status === 'partially_provisioned') && (
                              <button
                                onClick={() => {
                                  setProvisioningOrder(order);
                                  setActiveTab('provision-assignment');
                                }}
                                disabled={loading}
                                className="px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition disabled:opacity-50"
                              >
                                {order.status === 'partially_provisioned' ? 'Provision More' : 'Provision Now'}
                              </button>
                            )}
                            {order.status === 'provisioning' && (
                              <span className="text-sm text-gray-500 italic">Processing...</span>
                            )}
                            {order.status === 'provisioned' && (
                              <button
                                onClick={() => setActiveTab('esims')}
                                className="px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition"
                              >
                                View eSIMs
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'plans' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-light">Browse Plans</h2>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by country..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
                    disabled={!hasAccess}
                  />
                </div>
              </div>

              <div className="border border-gray-200">
                <table className="w-full">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="text-left p-4 text-xs font-medium text-gray-500">COUNTRY</th>
                      <th className="text-left p-4 text-xs font-medium text-gray-500">PLANS AVAILABLE</th>
                      <th className="text-left p-4 text-xs font-medium text-gray-500">STARTING FROM</th>
                      <th className="text-right p-4 text-xs font-medium text-gray-500">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Group plans by country
                      const countriesMap = new Map();
                      filteredPlans.forEach(plan => {
                        if (!countriesMap.has(plan.countryCode)) {
                          countriesMap.set(plan.countryCode, {
                            countryCode: plan.countryCode,
                            countryName: plan.countryName,
                            plans: []
                          });
                        }
                        countriesMap.get(plan.countryCode).plans.push(plan);
                      });

                      // Convert to array and calculate stats
                      const countries = Array.from(countriesMap.values()).map(country => ({
                        ...country,
                        planCount: country.plans.length,
                        startingPrice: Math.min(...country.plans.map(p => parseFloat(p.finalPrice)))
                      }));

                      return countries.map((country) => (
                        <tr key={country.countryCode} className="border-b border-gray-200 last:border-0">
                          <td className="p-4">
                            <div className="text-sm font-medium">{country.countryName}</div>
                            <div className="text-xs text-gray-500">{country.countryCode}</div>
                          </td>
                          <td className="p-4 text-sm">{country.planCount} plan{country.planCount !== 1 ? 's' : ''}</td>
                          <td className="p-4 text-sm">${country.startingPrice.toFixed(2)}</td>
                          <td className="p-4 text-right">
                            <button
                              onClick={() => {
                                if (!hasAccess) {
                                  setShowPaymentModal(true);
                                } else {
                                  setSelectedCountryPlans(country);
                                  setShowCountryPlansModal(true);
                                }
                              }}
                              className="text-sm hover:underline"
                            >
                              Order
                            </button>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

{/* eSIMs Tab */}
{activeTab === 'esims' && !viewingEsimDetails && (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-2xl font-light">My eSIMs</h2>
        <p className="text-sm text-gray-500 mt-1">
          {purchasedEsims.length} total • {purchasedEsims.filter(e => e.assignedEmail).length} assigned
        </p>
      </div>
      <button
        className="px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition"
        disabled={!hasAccess}
      >
        Export CSV
      </button>
    </div>

    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search by ICCID or email..."
        className="w-full pl-10 pr-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
        disabled={!hasAccess}
      />
    </div>

{purchasedEsims.length === 0 ? (
  <div className="border border-gray-200 p-16 text-center">
    <p className="text-sm text-gray-500 mb-2">No eSIMs provisioned yet</p>
    <p className="text-xs text-gray-400">Create a bulk order and provision it to see eSIMs here</p>
  </div>
) : (
  <div className="border border-gray-200">
    <table className="w-full">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Assigned To</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Plan</th>
          <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {purchasedEsims
          .filter(esim => 
            esim.iccid?.includes(searchTerm) || 
            esim.assignedEmail?.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map(esim => (
            <tr key={esim.id} className="hover:bg-gray-50 transition">
              <td className="px-6 py-4 text-sm">
                {esim.assignedEmail || (
                  <span className="text-gray-400">Unassigned</span>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">{esim.planName}</div>
                <div className="text-xs text-gray-500">{esim.country}</div>
              </td>
<td className="px-6 py-4 text-right">
  <div className="flex items-center justify-end gap-2">
    <button 
      onClick={() => handleViewEsimDetails(esim)}
      className="px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition"
    >
      View Details
    </button>
    <button 
      onClick={() => handleDownloadQR(esim)}
      className="px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition"
    >
      Download QR
    </button>
    {/* Add Revoke Button */}
    {esim.status !== 'revoked' && (
      <button 
        onClick={() => handleRevokeEsim(esim)}
        disabled={loading}
        className="px-4 py-2 border border-red-200 text-red-600 text-sm hover:bg-red-50 transition disabled:opacity-50"
      >
        Revoke
      </button>
    )}
  </div>
</td>
            </tr>
          ))}
      </tbody>
    </table>
  </div>
)}
  </div>
)}

{/* eSIM Details Screen */}
{activeTab === 'esims' && viewingEsimDetails && selectedEsimForDetails && (
  <EsimDetailsScreen
  handleRevokeEsim={handleRevokeEsim}
  handleBusinessDownloadQR={handleBusinessDownloadQR}
    onBack={() => {
      setViewingEsimDetails(false);
      setSelectedEsimForDetails(null);
    }}
    esim={selectedEsimForDetails}
    isLoading={false}
  />
)}
 
          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-light">Team Members</h2>
                <button
                  onClick={() => setShowAddMemberModal(true)}
                  className="px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition"
                  disabled={!hasAccess}
                >
                  Add Member
                </button>
              </div>

              {teamMembers.filter(member => member.status === 'active').length === 0 ? (
                <div className="border border-gray-200 p-12 text-center">
                  <p className="text-sm text-gray-500">No team members added yet</p>
                </div>
              ) : (
                <div className="border border-gray-200">
                  <table className="w-full">
                    <thead className="border-b border-gray-200">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">EMAIL</th>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">ROLE</th>
                        <th className="text-left p-4 text-xs font-medium text-gray-500">ADDED</th>
                        <th className="text-right p-4 text-xs font-medium text-gray-500">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembers
                        .filter(member => member.status === 'active')
                        .map(member => (
                          <tr key={member.id} className="border-b border-gray-200 last:border-0">
                            <td className="p-4 text-sm">{member.email}</td>
                            <td className="p-4">
                              <span className={`text-xs uppercase px-2 py-1 rounded ${
                                member.role === 'editor' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {member.role || 'reader'}
                              </span>
                            </td>
                            <td className="p-4 text-sm">
                              {member.addedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                            </td>
                            <td className="p-4 text-right">
                              <button
                                onClick={() => handleRemoveTeamMember(member.id)}
                                className="text-sm text-red-600 hover:underline"
                                disabled={!hasAccess}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'api-keys' && (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-2xl font-light">API Keys</h2>
        <p className="text-sm text-gray-500 mt-1">
          Generate API keys for programmatic access to bulk orders and team management
        </p>
      </div>
      <button
        onClick={() => setShowApiKeyModal(true)}
        className="px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition"
        disabled={!hasAccess}
      >
        Generate New Key
      </button>
    </div>

    {!hasAccess && (
      <div className="border border-gray-200 p-8 text-center">
        <Lock className="w-8 h-8 text-gray-400 mx-auto mb-4" />
        <p className="text-sm text-gray-600">
          Unlock business access to generate API keys
        </p>
      </div>
    )}

    {hasAccess && apiKeys.length === 0 && (
      <div className="border border-gray-200 p-12 text-center">
        <p className="text-sm text-gray-500">No API keys generated yet</p>
      </div>
    )}

    {hasAccess && apiKeys.length > 0 && (
      <div className="border border-gray-200">
        <table className="w-full">
          <thead className="border-b border-gray-200">
            <tr>
              <th className="text-left p-4 text-xs font-medium text-gray-500">NAME</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500">KEY PREVIEW</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500">STATUS</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500">USAGE</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500">CREATED</th>
              <th className="text-left p-4 text-xs font-medium text-gray-500">EXPIRES</th>
              <th className="text-right p-4 text-xs font-medium text-gray-500">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map(key => (
              <tr key={key.id} className="border-b border-gray-200 last:border-0">
                <td className="p-4 text-sm font-medium">{key.name}</td>
                <td className="p-4 text-sm font-mono text-gray-500">{key.keyPreview}</td>
                <td className="p-4">
                  <span className={`text-xs uppercase px-2 py-1 rounded ${
                    key.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {key.isActive ? 'Active' : 'Revoked'}
                  </span>
                </td>
                <td className="p-4 text-sm">{key.usageCount} calls</td>
                <td className="p-4 text-sm text-gray-500">
                  {new Date(key.createdAt).toLocaleDateString()}
                </td>
                <td className="p-4 text-sm text-gray-500">
                  {new Date(key.expiresAt).toLocaleDateString()}
                </td>
                <td className="p-4 text-right">
                  {key.isActive && (
                    <button
                      onClick={() => handleRevokeApiKey(key.id)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* API Documentation */}
    {hasAccess && (
      <div className="border border-gray-200 p-6 bg-gray-50">
        <h3 className="text-sm font-medium mb-3">Using Your API Key</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>Include your API key in the request header:</p>
          <code className="block bg-white p-3 rounded border border-gray-200 font-mono text-xs">
            X-API-Key: your_api_key_here
          </code>
          <p className="mt-4">Example: Create bulk order via API</p>
          <code className="block bg-white p-3 rounded border border-gray-200 font-mono text-xs whitespace-pre">
{`curl -X POST https://olahbackend.gaia-ml.com/api/business/bulk-orders/ \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "planName": "5GB - 7 Days",
    "country": "United States",
    "countryCode": "US",
    "duration": 7,
    "planPrice": 49.99,
    "planType": "fixed",
    "dataAmount": "5GB",
    "quantity": 10
  }'`}
          </code>
        </div>
      </div>
    )}
  </div>
          )}

          {activeTab === 'provision-assignment' && provisioningOrder && (
            <ProvisionAssignmentScreen
              onBack={() => {
                setProvisioningOrder(null);
                setActiveTab('orders');
              }}
              onProvision={handleProvisionOrder}
              order={provisioningOrder}
              isProvisioning={isProvisioning}  // ADD this line
            />
          )}
        </div>
      </main>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <AddMemberModal 
          onClose={() => setShowAddMemberModal(false)} 
          onAdd={handleAddTeamMember} 
        />
      )}

      {/* Bulk Order Modal */}
      {showBulkOrderModal && (
        <BulkOrderModal 
          onClose={() => {
            setShowBulkOrderModal(false);
            setPreselectedOrderData(null);
          }} 
          onOrder={handleBulkOrder} 
          plans={availablePlans}
          preselectedData={preselectedOrderData}
        />
      )}

    {/* Country Plans Modal */}
    {showCountryPlansModal && selectedCountryPlans && (
      <CountryPlansModal 
        onClose={() => {
          setShowCountryPlansModal(false);
          setSelectedCountryPlans(null);
        }}
        country={selectedCountryPlans}
        onSelectPlan={(plan, quantity) => {
          setPreselectedOrderData({ plan, quantity });
          setShowCountryPlansModal(false);
          setSelectedCountryPlans(null);
          setShowBulkOrderModal(true);
        }}
      />
    )}

    {showApiKeyModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium">
              {generatedApiKey ? 'API Key Generated' : 'Generate API Key'}
            </h3>
            <button onClick={() => {
              setShowApiKeyModal(false);
              setGeneratedApiKey(null);
            }}>
              <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          <div className="p-6">
            {!generatedApiKey ? (
              <ApiKeyForm
                onGenerate={handleGenerateApiKey}
                onClose={() => setShowApiKeyModal(false)}
              />
            ) : (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
                  <p className="text-sm text-yellow-800 font-medium mb-2">
                    ⚠️ Save this key now - it will not be shown again
                  </p>
                  <p className="text-xs text-yellow-700">
                    Copy and store this API key securely. You won't be able to see it again.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
                    Your API Key
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={generatedApiKey}
                      readOnly
                      className="flex-1 px-4 py-2 border border-gray-200 text-sm font-mono bg-gray-50"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedApiKey);
                        showNotification('API key copied to clipboard');
                      }}
                      className="px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setShowApiKeyModal(false);
                    setGeneratedApiKey(null);
                  }}
                  className="w-full px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    </div>
  );
};

// Add Member Modal Component
const AddMemberModal = ({ onClose, onAdd }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('reader'); // Default to reader
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    await onAdd(email, role);
    setLoading(false);
    setEmail('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium">Add Team Member</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
              placeholder="colleague@company.com"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
              Role
            </label>
            <div className="space-y-3">
              <label className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="reader"
                  checked={role === 'reader'}
                  onChange={(e) => setRole(e.target.value)}
                  className="mt-1 mr-3"
                  disabled={loading}
                />
                <div>
                  <div className="text-sm font-medium">Reader</div>
                  <div className="text-xs text-gray-500">
                    Can view eSIMs and download QR codes
                  </div>
                </div>
              </label>
              
              <label className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="editor"
                  checked={role === 'editor'}
                  onChange={(e) => setRole(e.target.value)}
                  className="mt-1 mr-3"
                  disabled={loading}
                />
                <div>
                  <div className="text-sm font-medium">Editor</div>
                  <div className="text-xs text-gray-500">
                    Can create orders, provision eSIMs, and manage team
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Member'}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bulk Order Modal Component
const BulkOrderModal = ({ onClose, onOrder, plans, preselectedData }) => {
  const [formData, setFormData] = useState({
    selectedPlan: preselectedData?.plan?.name || '',
    quantity: preselectedData?.quantity || 10,
    emails: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const selectedPlanData = plans.find(p => p.name === formData.selectedPlan);

  const handleProceedToPayment = async () => {
    // Validation
    if (formData.quantity < 10) {
      alert('Minimum order quantity is 10 eSIMs');
      return;
    }

    if (!formData.selectedPlan || formData.quantity < 1) return;

    setLoading(true);

    try {
      const emailList = formData.emails
        .split(/[\n,]/)
        .map(e => e.trim())
        .filter(e => e);

      const orderData = {
        planName: selectedPlanData.name,
        country: selectedPlanData.countryName,
        countryCode: selectedPlanData.countryCode,
        duration: selectedPlanData.duration,
        planPrice: selectedPlanData.finalPrice,
        planType: selectedPlanData.type,
        dataAmount: selectedPlanData.dataAmount > 0 ? (selectedPlanData.dataAmount / 1000).toFixed(1) : 'unlimited',
        quantity: parseInt(formData.quantity),
        assignedEmails: emailList,
        totalAmount: parseFloat(selectedPlanData.finalPrice) * parseInt(formData.quantity)
      };

      // Create Stripe payment intent
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${BASE_URL}/business/bulk-orders/create-payment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const data = await response.json();
        setClientSecret(data.client_secret);
        setShowPaymentForm(true);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('Failed to process order');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      setLoading(true);
      
      const emailList = formData.emails
        .split(/[\n,]/)
        .map(e => e.trim())
        .filter(e => e);

      const orderData = {
        planName: selectedPlanData.name,
        country: selectedPlanData.countryName,
        countryCode: selectedPlanData.countryCode,
        duration: selectedPlanData.duration,
        planPrice: selectedPlanData.finalPrice,
        planType: selectedPlanData.type,
        dataAmount: selectedPlanData.dataAmount > 0 ? (selectedPlanData.dataAmount / 1000).toFixed(1) : 'unlimited',
        quantity: parseInt(formData.quantity),
        assignedEmails: emailList,
        totalAmount: parseFloat(selectedPlanData.finalPrice) * parseInt(formData.quantity),
        paymentIntentId: paymentIntentId
      };

      // Complete the order
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${BASE_URL}/business/bulk-orders/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        alert('Bulk order created successfully!');
        onClose();
        window.location.reload();
      } else {
        alert('Order created but there was an error. Please contact support.');
      }
    } catch (error) {
      console.error('Error completing order:', error);
      alert('Failed to complete order');
    } finally {
      setLoading(false);
    }
  };

if (showPaymentForm && clientSecret) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium">Complete Payment</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <StripePaymentFormWrapper 
            clientSecret={clientSecret}
            totalAmount={parseFloat(selectedPlanData.finalPrice) * parseInt(formData.quantity)}
            onSuccess={handlePaymentSuccess}
            onCancel={() => {
              setShowPaymentForm(false);
              setClientSecret(null);
            }}
          />
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium">Create Bulk Order</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Only show plan selector if no preselected data */}
          {!preselectedData ? (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
                Select Plan
              </label>
              <select
                value={formData.selectedPlan}
                onChange={(e) => setFormData(prev => ({ ...prev, selectedPlan: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
                disabled={loading}
              >
                <option value="">Choose a plan...</option>
                {plans.slice(0, 50).map((plan, idx) => (
                  <option key={idx} value={plan.name}>
                    {plan.countryName} - {plan.type === 'unlimited' ? 'Unlimited' : `${(plan.dataAmount / 1000).toFixed(1)}GB`} - {plan.duration} days - ${plan.finalPrice}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {selectedPlanData && (
            <div className="border border-gray-200 p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">COUNTRY</div>
                  <div>{selectedPlanData.countryName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">DURATION</div>
                  <div>{selectedPlanData.duration} days</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">DATA</div>
                  <div>
                    {selectedPlanData.type === 'unlimited' ? 'Unlimited' : `${(selectedPlanData.dataAmount / 1000).toFixed(1)}GB`}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">UNIT PRICE</div>
                  <div>${selectedPlanData.finalPrice}</div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
              Quantity (Minimum 10)
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 10;
                setFormData(prev => ({ ...prev, quantity: Math.max(10, value) }));
              }}
              className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
              min="10"
              max="1000"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-2">
              Minimum order quantity is 10 eSIMs
            </p>
          </div>

          {selectedPlanData && formData.quantity >= 10 && (
            <div className="border border-gray-200 p-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">TOTAL AMOUNT</span>
                <span className="text-2xl font-light">
                  ${(parseFloat(selectedPlanData.finalPrice) * parseInt(formData.quantity)).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleProceedToPayment}
              disabled={loading || !selectedPlanData || formData.quantity < 10}
              className="flex-1 px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StripePaymentFormWrapper = ({ clientSecret, totalAmount, onSuccess, onCancel }) => {
  const options = {
    clientSecret,
    appearance: { theme: 'stripe' },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripePaymentForm 
        totalAmount={totalAmount}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
};

const StripePaymentForm = ({ totalAmount, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border border-gray-200 p-4 bg-gray-50">
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">TOTAL AMOUNT</div>
          <div className="text-3xl font-light">${totalAmount.toFixed(2)}</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 p-4 rounded">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* THIS IS THE KEY PART - Stripe Payment Element */}
      <PaymentElement />

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 px-4 py-3 bg-black text-white text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50"
        >
          {loading ? 'Processing...' : `Pay $${totalAmount.toFixed(2)}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-3 border border-gray-200 text-sm hover:bg-gray-50 transition disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const CountryPlansModal = ({ onClose, country, onSelectPlan }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quantity, setQuantity] = useState(10);

  // Filter to show only unlimited plans
  const unlimitedPlans = country.plans.filter(plan => 
    plan.type === 'unlimited' || plan.dataAmount === -1
  );

  const handleOrder = () => {
    if (!selectedPlan) return;
    onSelectPlan(selectedPlan, quantity);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-medium">{country.countryName} Plans</h3>
            <p className="text-xs text-gray-500 mt-1">{unlimitedPlans.length} unlimited plan{unlimitedPlans.length !== 1 ? 's' : ''} available</p>
          </div>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {unlimitedPlans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">No unlimited plans available for this country</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-3 uppercase">
                  Select an Unlimited Plan
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {unlimitedPlans.map((plan, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedPlan(plan)}
                      className={`text-left p-4 border transition ${
                        selectedPlan === plan
                          ? 'border-black bg-gray-50'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="space-y-2">
                        <div className="font-light">${plan.finalPrice}</div>
                        <div className="text-sm font-medium">
                          Unlimited
                        </div>
                        <div className="text-xs text-gray-500">
                          {plan.duration} days
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {selectedPlan && (
                <>
                  <div className="border-t border-gray-200 pt-6">
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">SELECTED PLAN</div>
                        <div className="font-medium">
                          Unlimited Data
                          {' • '}
                          {selectedPlan.duration} days
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">UNIT PRICE</div>
                        <div className="font-medium">${selectedPlan.finalPrice}</div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
                        Quantity (Minimum 10)
                      </label>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(10, parseInt(e.target.value) || 10))}
                        className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
                        min="10"
                        max="1000"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum order quantity is 10 eSIMs
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 p-4 bg-gray-50">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">TOTAL AMOUNT</span>
                      <span className="text-2xl font-light">
                        ${(parseFloat(selectedPlan.finalPrice) * quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleOrder}
                  disabled={!selectedPlan}
                  className="flex-1 px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition disabled:opacity-50"
                >
                  Continue to Order
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ApiKeyForm = ({ onGenerate, onClose }) => {
  const [keyName, setKeyName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!keyName.trim()) {
      return;
    }
    setLoading(true);
    await onGenerate(keyName);
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-2 uppercase">
          Key Name
        </label>
        <input
          type="text"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
          placeholder="e.g., Production API Key"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-2">
          Choose a descriptive name to identify this key
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={loading || !keyName.trim()}
          className="flex-1 px-4 py-2 bg-black text-white text-sm hover:bg-gray-800 transition disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate Key'}
        </button>
        <button
          onClick={onClose}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};


const ProvisionAssignmentScreen = ({ onBack, onProvision, order, isProvisioning }) => {
  const [assignments, setAssignments] = useState([{ email: '', quantity: 1 }]);
  const [organizationName, setOrganizationName] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const alreadyProvisioned = order.provisionedCount || 0;
  const totalQuantity = order.quantity;
  const remainingInOrder = totalQuantity - alreadyProvisioned;
  
  const assignedQuantity = assignments.reduce((sum, a) => {
    const qty = parseInt(a.quantity);
    return sum + (isNaN(qty) || qty < 1 ? 0 : qty);
  }, 0);

  const addAssignment = () => {
    setAssignments([...assignments, { email: '', quantity: 1 }]);
  };

  const removeAssignment = (index) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const updateAssignment = (index, field, value) => {
    const newAssignments = [...assignments];
    newAssignments[index][field] = value;
    setAssignments(newAssignments);
  };

  const handleSubmit = async () => {
    // Validate organization name
    if (!organizationName.trim()) {
      alert('Organization name is required');
      return;
    }

    // Validation
    const validAssignments = assignments.filter(a => a.email.trim() && a.quantity > 0);
    
    const totalAssigned = validAssignments.reduce((sum, a) => sum + parseInt(a.quantity), 0);
    
    if (totalAssigned > remainingInOrder) {
      alert(`You're trying to provision ${totalAssigned} eSIMs but only ${remainingInOrder} are available.`);
      return;
    }

    // Check for duplicate emails
    const emails = validAssignments.map(a => a.email.toLowerCase());
    if (new Set(emails).size !== emails.length) {
      alert('Duplicate emails found. Each email should appear only once.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const assignment of validAssignments) {
      if (!emailRegex.test(assignment.email)) {
        alert(`Invalid email: ${assignment.email}`);
        return;
      }
    }

    await onProvision(validAssignments, totalAssigned, organizationName, customMessage);
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      // Parse CSV: email,quantity
      const newAssignments = [];
      let totalQty = 0;
      
      for (const line of lines) {
        const [email, quantity] = line.split(',').map(s => s.trim());
        if (email && quantity) {
          const qty = parseInt(quantity) || 1;
          newAssignments.push({ email, quantity: qty });
          totalQty += qty;
        }
      }

      if (newAssignments.length > 0) {
        setAssignments(newAssignments);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          disabled={isProvisioning}
          className="p-2 hover:bg-gray-100 rounded transition disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h2 className="text-2xl font-light">Provision eSIMs</h2>
          <p className="text-sm text-gray-500 mt-1">
            Order #{order.id.substring(0, 8)} • {remainingInOrder} of {totalQuantity} eSIMs remaining
          </p>
        </div>
      </div>

      {/* Order Summary */}
      <div className="border border-gray-200 p-6">
        <div className="text-xs text-gray-500 mb-3 uppercase">Order Details</div>
        <div className="grid grid-cols-5 gap-6">
          <div>
            <div className="text-sm text-gray-500 mb-1">Plan</div>
            <div className="font-medium">{order.planName}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Country</div>
            <div className="font-medium">{order.country}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Total Ordered</div>
            <div className="font-medium">{totalQuantity} eSIMs</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Already Provisioned</div>
            <div className="font-medium text-green-600">{alreadyProvisioned} eSIMs</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Remaining</div>
            <div className="font-medium text-blue-600">{remainingInOrder} eSIMs</div>
          </div>
        </div>
      </div>

      {/* Organization Info Section - NEW */}
      <div className="border border-gray-200 p-6">
        <div className="text-xs text-gray-500 mb-3 uppercase">Organization Information</div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Organization Name <span className="text-red-500">*</span>
            </label>
<input
  type="text"
  value={organizationName}
  onChange={(e) => setOrganizationName(e.target.value)}
  placeholder="e.g., Tech Corp, Green Earth NGO, Annual Summit 2025"
  disabled={isProvisioning}  // ADD this
  className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400 disabled:bg-gray-50"
/>
            <p className="text-xs text-gray-500 mt-1">
              This will appear in the email as "Provided by [Organization Name]"
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Custom Message (Optional)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="e.g., For Tech Summit 2025 attendees. Enjoy your stay in Peru!"
              disabled={isProvisioning}
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400 disabled:bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Add a custom message for recipients (e.g., event details, instructions)
            </p>
          </div>
        </div>
      </div>

      {/* Import CSV */}
      <div className="border border-gray-200 p-6">
        <label className="block text-sm font-medium mb-3">
          Import from CSV (Optional)
        </label>
        <input
          type="file"
          accept=".csv"
          onChange={handleImportCSV}
          disabled={isProvisioning}
          className="text-sm"
        />
        <p className="text-xs text-gray-500 mt-2">
          CSV Format: email,quantity (one per line). Example:<br/>
          john@company.com,3<br/>
          sara@company.com,5
        </p>
      </div>

      {/* Email Assignments */}
      <div className="border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium">
            Email Assignments
          </label>
          <button
            onClick={addAssignment}
            disabled={isProvisioning}
            className="text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
          >
            + Add Recipient
          </button>
        </div>

        <div className="space-y-3">
          {assignments.map((assignment, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="email"
                  value={assignment.email}
                  onChange={(e) => updateAssignment(index, 'email', e.target.value)}
                  placeholder="recipient@company.com"
                  disabled={isProvisioning}
                  className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400 disabled:bg-gray-50"
                />
              </div>
              <div className="w-24">
                <input
                  type="number"
                  value={assignment.quantity}
                  onChange={(e) => updateAssignment(index, 'quantity', Math.max(1, Math.min(parseInt(e.target.value) || 1, remainingInOrder)))}
                  min="1"
                  max={remainingInOrder}
                  disabled={isProvisioning}
                  className="w-full px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400 disabled:bg-gray-50"
                />
              </div>
              <button
                onClick={() => removeAssignment(index)}
                disabled={assignments.length === 1 || isProvisioning}
                className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        {/* Show total summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total eSIMs to provision:</span>
            <span className="font-medium">{assignedQuantity} of {remainingInOrder} available</span>
          </div>
        </div>
      </div>

    {/* Actions */}
    <div className="flex gap-4 pt-4">
      <button
        onClick={handleSubmit}
        disabled={isProvisioning || !organizationName.trim() || assignedQuantity < 1 || assignedQuantity > remainingInOrder}
        className="flex-1 px-6 py-3 bg-black text-white text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProvisioning ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Provisioning...
          </span>
        ) : (
          `Provision ${assignedQuantity} eSIM${assignedQuantity !== 1 ? 's' : ''} Now`
        )}
      </button>
      <button
        onClick={onBack}
        disabled={isProvisioning}
        className="px-6 py-3 border border-gray-200 text-sm hover:bg-gray-50 transition disabled:opacity-50"
      >
        Cancel
      </button>
    </div>

    {/* Info Section */}
    <div className="border-t border-gray-200 pt-4">
      {isProvisioning ? (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded">
          <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Provisioning in progress... This may take 1-2 minutes. Please don't close this page.
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            ℹ️ <strong>What happens next:</strong>
          </p>
          <ul className="text-sm text-gray-600 mt-2 space-y-1 ml-4">
            <li>• {assignedQuantity} eSIM{assignedQuantity !== 1 ? 's' : ''} will be provisioned from the provider (~1-2 minutes)</li>
            <li>• Assigned email owners will receive activation emails with QR codes</li>
            <li>• Email will show "Provided by {organizationName || '[Your Organization]'}"</li>
            {customMessage && <li>• Your custom message will be included in the email</li>}
            <li>• You can return here to provision the remaining {remainingInOrder - assignedQuantity} eSIMs anytime</li>
          </ul>
        </>
      )}
    </div>
    </div>
  );
};

export default BusinessAdmin;